import React, {useEffect, useState, useMemo, useCallback} from "react";
import {inject, observer} from "mobx-react";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate} from "react-router-dom";
//locale
import {removeStorage} from "helpers/storage";
import OfferCardView from "./viewState/OfferCardView";
import OfferCardEdit from "./editState/OfferCardEdit";
import {hasPermission, getFullName} from "helpers/helper";
import {Preloader} from "components/Preloader";
import {toOfferId} from "constants/routes.const";
import {showSuccess} from "helpers/notifications.helper";

const OfferCard = inject("store")(
  observer(
    ({
      store: {auth, clientStore, buyer, seller, organization},
      offer,
      clientSide = "seller",
    }) => {
      const {language} = clientStore;
      const {user} = auth;
      const {request, offerFiles} = seller;
      const {currentRequest} = buyer;
      const {t} = useTranslation();
      const location = useLocation();
      const navigate = useNavigate();

      const [edit, setEdit] = useState(false);
      const [loading, setLoading] = useState(false);
      const [organizationMembers, setOrganizationMembers] = useState(null);
      const isSellerSide = useMemo(() => clientSide === "seller", [clientSide]);
      const create = useMemo(
        () => (isSellerSide ? !request?.Offer?.id : location.state?.offerId === "create"),
        [isSellerSide, request?.Offer, location]
      );
      const rfp = useMemo(
        () => (!isSellerSide && create ? currentRequest : offer?.Proposal?.Request),
        [isSellerSide, create, currentRequest, offer]
      );
      const requestId = useMemo(
        () =>
          isSellerSide
            ? request.RequestId ?? request.Request?.id
            : offer.id ?? location.state?.proposalId,
        [isSellerSide, request, location, offer]
      );
      const sellerOrganizationName = useMemo(
        () =>
          isSellerSide
            ? ""
            : offer?.Proposal?.Organization?.name ??
              currentRequest?.recipients?.registered?.find(
                (recipient) => (recipient.Offer?.id ?? recipient.proposalId) === requestId
              )?.name,
        [isSellerSide, offer, currentRequest, requestId]
      );
      const sellerOrganizationId = useMemo(
        () =>
          isSellerSide
            ? ""
            : offer?.Proposal?.Organization?.id ??
              currentRequest?.recipients?.registered?.find(
                (recipient) => (recipient.Offer?.id ?? recipient.proposalId) === requestId
              )?.Organization?.id,
        [isSellerSide, offer, currentRequest, requestId]
      );

      const sidesData = useMemo(
        () => ({
          buyer: {
            offer: {
              ...offer,
              requestId,
              requestName: rfp?.name,
              projectId: create ? rfp?.Project?.id : rfp?.ProjectItem?.Project?.id,
              projectName: create ? rfp?.Project?.name : rfp?.ProjectItem?.Project?.name,
              organizationId: sellerOrganizationId,
              organizationName: sellerOrganizationName,
              CurrencyCode: rfp?.CurrencyCode,
              UserAssignee: offer?.Proposal?.UserAssignee,
            },
            offerFiles: offer?.OfferFiles,
            data: {
              description: {
                title: t("OFFER_CARD_VIEW_BUYER_DESCRIPTION_TITLE")?.format(
                  sellerOrganizationName
                ),
                message: offer?.message,
                placeholder: "OFFER_CARD_VIEW_BUYER_DESCRIPTION_PLACEHOLDER",
              },
              editPermission: "EDIT_RFT_OFFERS_OFFER",
              editButtonWrapperClass: " pos-a top-17 mla fit-width",
              needButtonPrint: true,
              organizationMembers,
              labels: {
                submitted: "OFFER_CARD_VIEW_BUYER_LABEL_SUBMITTED",
                submitButton: create
                  ? "OFFER_CARD_VIEW_BUYER_LABEL_CREATE_BUTTON"
                  : "OFFER_CARD_VIEW_BUYER_LABEL_UPDATE_BUTTON",
                validUntil: "OFFER_CARD_VIEW_BUYER_LABEL_VALID_UNTIL",
                validUntilPlaceholder:
                  "OFFER_CARD_VIEW_BUYER_LABEL_VALID_UNTIL_PLACEHOLDER",
                price: "OFFER_CARD_VIEW_BUYER_LABEL_PRICE",
                pricePlaceholder: "OFFER_CARD_VIEW_BUYER_LABEL_PRICE_PLACEHOLDER",
                editButton: "OFFER_CARD_VIEW_BUYER_LABEL_EDIT_BUTTON",
                contact: "SELLER_CONTACT",
                contactPlaceholder: "OFFER_CARD_VIEW_BUYER_LABEL_CONTACT_PLACEHOLDER",
                documents: "DOCUMENTS",
                documentsPlaceholder: "PLACEHOLDER_DOCUMENT_COLUMN_EXTENDED",
              },
            },
          },
          seller: {
            offer: {
              ...request?.Offer,
              CurrencyCode: request?.Request?.CurrencyCode,
              smartFormQuestions: request?.Request?.smartFormQuestions,
              requestId,
            },
            offerFiles: offerFiles,
            data: {
              description: {
                title: t("OFFER_CARD_VIEW_SELLER_DESCRIPTION_TITLE"),
                message: request?.Offer?.message,
                placeholder: "OFFER_CARD_VIEW_SELLER_DESCRIPTION_PLACEHOLDER",
              },
              editPermission: "READ_SELLER_RFTS",
              editButtonWrapperClass: "",
              needButtonPrint: false,
              labels: {
                submitted: "OFFER_CARD_VIEW_SELLER_LABEL_SUBMITTED",
                submitButton: create
                  ? "OFFER_CARD_VIEW_SELLER_LABEL_CREATE_BUTTON"
                  : "OFFER_CARD_VIEW_SELLER_LABEL_UPDATE_BUTTON",
                validUntil: "OFFER_CARD_VIEW_SELLER_LABEL_VALID_UNTIL",
                validUntilPlaceholder:
                  "OFFER_CARD_VIEW_SELLER_LABEL_VALID_UNTIL_PLACEHOLDER",
                price: "OFFER_CARD_VIEW_SELLER_LABEL_PRICE",
                pricePlaceholder: "OFFER_CARD_VIEW_SELLER_LABEL_PRICE_PLACEHOLDER",
                editButton: "OFFER_CARD_VIEW_SELLER_LABEL_EDIT_BUTTON",
                documents: "TENDER_DOCUMENT",
                documentsPlaceholder: "PLACEHOLDER_DOCUMENT_COLUMN_EXTENDED",
              },
            },
          },
        }),
        [
          request,
          offer,
          offerFiles,
          rfp,
          requestId,
          create,
          t,
          sellerOrganizationName,
          organizationMembers,
          sellerOrganizationId,
        ]
      );

      const openEdit = useCallback(
        () => hasPermission([sidesData[clientSide].data.editPermission]) && setEdit(true),
        [sidesData, clientSide]
      );

      const updateAfterSave = useCallback(
        (id) => {
          setLoading(false);
          setEdit(false);
          requestId && removeStorage(`requestOffer-${requestId}`);
          isSellerSide && seller.getRequest(id ?? request.id);
        },
        [isSellerSide, request, requestId, seller]
      );

      const handleSubmit = useCallback(
        (payload) => {
          if (!hasPermission([sidesData[clientSide].data.editPermission])) return;
          setLoading(true);
          create
            ? (isSellerSide
                ? seller.createOurOffer(payload)
                : buyer.createOffer(payload, {id: location.state?.proposalId})
              )
                .then((data) => {
                  isSellerSide
                    ? updateAfterSave(data.ProposalId)
                    : navigate(toOfferId(rfp?.id, data.data.id), {replace: true});
                  showSuccess(t("OFFER_CREATED"));
                })
                .catch(() => updateAfterSave())
            : (isSellerSide ? seller.updateOurOffer(payload) : buyer.saveOffer(payload))
                .then(() => showSuccess(t("OFFER_UPDATED")))
                .finally(updateAfterSave);
        },
        [
          buyer,
          clientSide,
          create,
          isSellerSide,
          location.state?.proposalId,
          navigate,
          seller,
          sidesData,
          rfp?.id,
          t,
          updateAfterSave,
        ]
      );

      useEffect(() => {
        if (
          isSellerSide &&
          !!request?.Offer?.id &&
          request.Offer?.id !== seller.currentOfferId
        )
          seller.getOffer(seller.currentOfferId);
      }, [seller, isSellerSide, seller.currentOfferId, request]);

      useEffect(() => {
        !!sellerOrganizationId &&
          organization
            .getAnotherOrganizationMembers(
              sellerOrganizationId,
              "SHOW_RFT_RECIPIENTS_RECIPIENT_NOTIFY_LIST_USER"
            )
            .then((members) =>
              setOrganizationMembers(
                members
                  ?.map((member) => ({
                    value: member.id,
                    label: getFullName(member?.firstName, member?.lastName),
                  }))
                  .filter((val) => val.label)
              )
            );
      }, [organization, sellerOrganizationId]);

      return (isSellerSide ? !seller.offerLoading : true) ? (
        edit || create ? (
          <OfferCardEdit
            request={request}
            offer={sidesData[clientSide].offer}
            offerFiles={sidesData[clientSide].offerFiles}
            sideData={sidesData[clientSide].data}
            mode={create ? "create" : "edit"}
            setEdit={setEdit}
            setCreate={() => seller.setActiveRequestTab(0)}
            side={clientSide}
            loading={loading}
            uploadFile={(values) => seller.uploadSellerFile(values)}
            updateDocuments={(OfferFiles) =>
              isSellerSide
                ? seller.updateOfferDocuments(OfferFiles)
                : buyer.updateOffer({...sidesData[clientSide].offer, OfferFiles})
            }
            submit={handleSubmit}
          />
        ) : (
          <OfferCardView
            offer={sidesData[clientSide].offer}
            offerFiles={sidesData[clientSide].offerFiles}
            language={language}
            user={user}
            clickEdit={openEdit}
            sideData={sidesData[clientSide].data}
          />
        )
      ) : (
        <Preloader />
      );
    }
  )
);

export default OfferCard;
